import type { TimeFrame } from 'components/AnimatedTimelineControl';
import type { TerrierManifest, TerrierOverlay } from '../vendor/terrier';
import { generateFramesFromSlices } from './generateFrames';

export interface ManifestLoadResult {
  timeRange: { min: number; max: number };
  frameCount: number;
  frames: TimeFrame[];
}

/**
 * Process a Terrier manifest and extract time range and frames.
 * Returns null if manifest should be ignored (wrong region or empty).
 */
export const processManifest = (
  manifest: TerrierManifest,
  maxForecastSeconds: number,
): ManifestLoadResult | null => {
  const maxEpoch = (Date.now() + maxForecastSeconds * 1000) / 1000;
  const timeSlices = manifest.timeSlices.filter(
    (slice) => slice.forecastEpoch <= maxEpoch,
  );
  if (timeSlices.length === 0) return null;

  const firstSlice = timeSlices[0];
  const lastSlice = timeSlices[timeSlices.length - 1];

  return {
    timeRange: { min: firstSlice.forecastEpoch, max: lastSlice.forecastEpoch },
    frameCount: timeSlices.length,
    frames: generateFramesFromSlices(timeSlices),
  };
};

type ManifestCallbacks = {
  setTimeData: (min: number, max: number, frameCount: number) => void;
  setFrames: (frames: TimeFrame[]) => void;
  jumpToFrame: (index: number) => void;
};

const manifestKey = (manifest: TerrierManifest): string =>
  `${manifest.source}:${manifest.region}:${manifest.variable ?? ''}`;

const mergeFrames = (framesByKey: Map<string, TimeFrame[]>): TimeFrame[] => {
  const seen = new Set<number>();
  const merged: TimeFrame[] = [];
  for (const frames of framesByKey.values()) {
    for (const frame of frames) {
      const epoch = frame.timestamp.getTime();
      if (!seen.has(epoch)) {
        seen.add(epoch);
        merged.push(frame);
      }
    }
  }
  merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  return merged;
};

/**
 * Returns a loadCallback that aggregates frames across multiple sources/regions.
 * Each call replaces that source's frames and re-merges into a sorted, deduplicated list.
 */
export const createManifestLoadHandler = (
  overlay: TerrierOverlay,
  getIsPlaying: () => boolean,
  maxForecastSeconds: number,
  callbacks: ManifestCallbacks,
): ((manifest: TerrierManifest) => void) => {
  const framesByKey = new Map<string, TimeFrame[]>();

  return (manifest) => {
    const result = processManifest(manifest, maxForecastSeconds);
    if (!result) return;

    framesByKey.set(manifestKey(manifest), result.frames);

    const allFrames = mergeFrames(framesByKey);
    const min = allFrames[0].timestamp.getTime() / 1000;
    const max = allFrames[allFrames.length - 1].timestamp.getTime() / 1000;

    callbacks.setTimeData(min, max, allFrames.length);
    callbacks.setFrames(allFrames);

    if (!getIsPlaying()) {
      const nowMs = Date.now();
      // Find the last frame at or before now
      let nowIdx = 0;
      for (let i = 0; i < allFrames.length; i += 1) {
        if (allFrames[i].timestamp.getTime() <= nowMs) {
          nowIdx = i;
        }
      }
      overlay.setCurrentTime(allFrames[nowIdx].timestamp.getTime() / 1000);
      callbacks.jumpToFrame(nowIdx);
    }
  };
};
